// src/encounter/ambient/ambient-start-card.tsx
//
// `ambientWindow` (`?x=ambient-window`, src/shared/lib/encounter-flags.ts) · the ambient "Start listening" card, as Antonin's catalog
// draws it (scrips-signal-ds feat/signal @62cb3650, components/AmbientMic: the idle card, the live row, the disabled no-consent card;
// placed by pages/WorkspacePage as its own row above the first zone, right-aligned). It replaces the small page-header mic.
//
// It only draws. The state and the Start / Stop calls are the ambient panel's own (`AmbientScribePanel` `onRecordingControl`), the
// same handlers the header mic called, recording-consent gate included: the panel leaves `start` out while the consent blocks a start,
// and the card is then disabled with the consent check's own words. Consent is asked at the start only; nothing here writes or fills.
//
// The mark is Antonin's IntelOrb (@scripsteam/signal) with the mic glyph, exactly as AmbientMic draws it (Antonin 29 Oct: "the mic is
// Intelligence too: the orb with its dot globe around the mic"), inside SignalScope for his tokens. It replaced the DS OrbIndicator
// (the bracket mark on a dark disc), which Samer called big and broken (4 Nov).
// PA difference from the catalog (listed for Antonin): PA's recording has Stop, not Pause / Resume (no pause in the ambient session),
// so the live row offers Stop.
import { Button } from '@scripsteam/scrips-signal-ds';
import { IntelOrb } from '@scripsteam/signal';
import { MessageSquareText, Mic, Square } from 'lucide-react';
import { SignalScope } from '../nurse-med-rec/nurse-med-rec-block';
import type { AmbientRecordingControl } from './AmbientScribePanel';
import { recordingControlAtRest } from './recording-control-at-rest';
import { RECORDING_CONSENT_CHECKING_COPY, RECORDING_CONSENT_DENIED_COPY, type RecordingConsentStatus } from './recording-consent-types';
import { clockOf } from '../intelligence/rail-words';
import { AMBIENT_START_WORDS } from './ambient-window-words';

const CARD = 'flex items-center gap-3 rounded-lg border border-border-default bg-surface-card py-2 pr-4 pl-2 text-left';

/** Antonin's AmbientMic mark: IntelOrb, 48, mic glyph; `listening` while the recording runs. */
function AmbientOrb({ listening = false }: { listening?: boolean }) {
  return (
    <SignalScope slot="ambient-orb">
      <IntelOrb size={48} state={listening ? 'listening' : 'idle'} glyph={<Mic aria-hidden />} />
    </SignalScope>
  );
}

export function AmbientStartCard({
  control,
  consent,
  onOpen,
}: {
  control: AmbientRecordingControl;
  /** The recording-consent check (RecordingConsentHost), for the words beside a disabled Start. */
  consent?: RecordingConsentStatus;
  /** Opens the ambient window: after Start, and from Open transcript. */
  onOpen: () => void;
}) {
  const live = control.status === 'recording' || control.starting;
  if (live) {
    return (
      <div data-slot="ambient-start-card" data-state="live" className={CARD}>
        <AmbientOrb listening />
        <span role="status" className="flex min-w-0 flex-col">
          <span className="text-sm font-medium text-text-primary">
            {control.starting && control.status !== 'recording' ? AMBIENT_START_WORDS.starting : AMBIENT_START_WORDS.listening}
          </span>
          <span className="text-xs text-text-secondary tabular-nums">{clockOf(control.elapsed)}</span>
        </span>
        <span className="ml-2 flex gap-1">
          <Button variant="tertiary" size="sm" className="text-text-secondary" icon={<Square className="size-4" aria-hidden />} onClick={control.stop}>
            {AMBIENT_START_WORDS.stop}
          </Button>
          <Button
            variant="tertiary"
            size="sm"
            className="text-text-secondary"
            icon={<MessageSquareText className="size-4" aria-hidden />}
            aria-label={AMBIENT_START_WORDS.transcript}
            title={AMBIENT_START_WORDS.transcript}
            onClick={onOpen}
          />
        </span>
      </div>
    );
  }
  // Start only at rest (idle, or a finished recording the panel offers to record again: `restable`), and only when the consent
  // gate allows it (the panel leaves `start` out otherwise).
  const atRest = recordingControlAtRest(control);
  const canStart = atRest && !!control.start;
  const blockedBy =
    atRest && !control.start
      ? consent === 'denied'
        ? RECORDING_CONSENT_DENIED_COPY
        : consent === 'checking'
          ? RECORDING_CONSENT_CHECKING_COPY
          : undefined
      : undefined;
  return (
    <button
      type="button"
      data-slot="ambient-start-card"
      data-state={canStart ? 'idle' : 'disabled'}
      className={`${CARD} cursor-pointer disabled:cursor-default disabled:opacity-60`}
      disabled={!canStart}
      aria-label={blockedBy ? `${AMBIENT_START_WORDS.start}: ${blockedBy}` : undefined}
      onClick={() => {
        control.start?.();
        onOpen();
      }}
    >
      <AmbientOrb />
      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-medium text-text-primary">{AMBIENT_START_WORDS.start}</span>
        <span className="text-xs text-text-secondary">{blockedBy ?? AMBIENT_START_WORDS.sub}</span>
      </span>
    </button>
  );
}

/**
 * B1 (Andrew, 3 Nov): the same card, in the same place, on the pre-visit. Before Start there is no ambient session, so its one press
 * runs the existing Start encounter and THEN starts listening, once the encounter is open (the page does that, with the panel's own
 * start and consent gate). Said on the button, so nothing happens that it does not say. While Start is refused, it is disabled with
 * Start's own reason; a refusal after the press starts nothing.
 */
export function AmbientStartWithEncounterCard({ disabledReason, onStart }: { disabledReason?: string; onStart: () => void }) {
  return (
    <button
      type="button"
      data-slot="ambient-start-card"
      data-state={disabledReason ? 'disabled' : 'pre-visit'}
      className={`${CARD} cursor-pointer disabled:cursor-default disabled:opacity-60`}
      disabled={!!disabledReason}
      aria-label={disabledReason ? `${AMBIENT_START_WORDS.startWithEncounter}: ${disabledReason}` : undefined}
      onClick={onStart}
    >
      <AmbientOrb />
      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-medium text-text-primary">{AMBIENT_START_WORDS.startWithEncounter}</span>
        <span className="text-xs text-text-secondary">{disabledReason ?? AMBIENT_START_WORDS.subWithEncounter}</span>
      </span>
    </button>
  );
}
